import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback } from 'react';
import { EditorContent } from '@tiptap/react';
import { FileDrop } from 'react-file-drop';
import { useEmbed } from '@tribeplatform/react-sdk/hooks';
import { AppsEnum } from '../common/constants/app';
import { useI18n } from '../i18n';
import { AudioPickerModal } from '../Picker/AudioPickerModal';
import { GiphyPickerModal } from '../Picker/GiphyPickerModal';
import { ImagePickerModal } from '../Picker/ImagePickerModal';
import { VideoPickerModal } from '../Picker/VideoPickerModal';
import { EmbedFormDialog } from './components/EmbedFormDialog';
import { LinkFormDialog } from './components/LinkFormDialog';
import { TextFormattingBubbleMenu } from './extensions/bubble-menu';
import { LinkFormattingBubbleMenu } from './extensions/bubble-menu/link';
import { useComposer } from './hooks/useComposer';
import { composerError } from './utils/errors';
export const Composer = ({ children }) => {
    const intl = useI18n();
    const { asyncQuery: embed } = useEmbed();
    const { state, toggleDialog, editor, onFileUpload } = useComposer();
    const onEditorClick = useCallback(() => {
        if (editor && editor.isEmpty && !editor.isFocused) {
            editor.commands.focus();
        }
    }, [editor]);
    const onEmbed = useCallback(async (data) => {
        const { link } = data;
        if (link) {
            const oembed = await embed({ url: link });
            if (oembed.html) {
                const { from } = editor.state.selection;
                editor
                    .chain()
                    .focus()
                    .setEmbed({ embed: oembed })
                    .setTextSelection(from + 1)
                    .insertContent(' ')
                    .run();
            }
        }
    }, [editor, embed]);
    if (!editor) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "h-full relative flex-col flex flex-1", children: [_jsx(TextFormattingBubbleMenu, { editor: editor }, void 0), _jsx(LinkFormattingBubbleMenu, { editor: editor }, void 0), _jsx(FileDrop, { className: "flex flex-col flex-grow", targetClassName: "flex flex-col flex-grow", onDrop: files => onFileUpload(files), children: _jsx("article", { className: "flex flex-col flex-grow editor-content", children: _jsx(EditorContent, { editor: editor, onClick: onEditorClick, className: "text-md text-content relative flex-1 mb-4 flex flex-col" }, void 0) }, void 0) }, void 0), children] }, void 0), _jsx(ImagePickerModal, { open: state.image, multiple: true, close: () => toggleDialog('image', false), upload: onFileUpload }, void 0), _jsx(GiphyPickerModal, { open: state.giphy, close: () => toggleDialog('giphy', false), onUpload: file => {
                    onFileUpload([file], { attrs: { app: AppsEnum.Giphy } });
                    toggleDialog('giphy', false);
                } }, void 0), _jsx(VideoPickerModal, { open: state.video, close: () => toggleDialog('video', false), onUpload: files => {
                    onFileUpload(files);
                    toggleDialog('video', false);
                }, onEmbed: async (link) => {
                    try {
                        await onEmbed({ link });
                        toggleDialog('video', false);
                    }
                    catch (e) {
                        composerError(intl)(intl.$t({
                            id: 'Composer.Composer.InvalidUrl',
                            defaultMessage: 'The url that you entered is not valid',
                        }));
                    }
                } }, void 0), _jsx(AudioPickerModal, { open: state.audio, close: () => toggleDialog('audio', false), onUpload: files => {
                    onFileUpload(files);
                    toggleDialog('audio', false);
                }, onEmbed: async (link) => {
                    try {
                        await onEmbed({ link });
                        toggleDialog('audio', false);
                    }
                    catch (e) {
                        composerError(intl)(intl.$t({
                            id: 'Composer.Composer.InvalidUrl',
                            defaultMessage: 'The url that you entered is not valid',
                        }));
                    }
                } }, void 0), _jsx(LinkFormDialog, { open: state.link, onClose: () => toggleDialog('link', false), onSubmit: data => {
                    const { link, text } = data;
                    if (link) {
                        editor
                            .chain()
                            .focus()
                            .insertLink({ href: link, text })
                            .insertContent(' ')
                            .run();
                    }
                    toggleDialog('link', false);
                } }, void 0), _jsx(EmbedFormDialog, { embedItem: state.embedItem, open: state.embed, onClose: () => toggleDialog('embed', false), onSubmit: async (data, methods) => {
                    try {
                        await onEmbed(data);
                        toggleDialog('embed', false);
                    }
                    catch (e) {
                        methods === null || methods === void 0 ? void 0 : methods.setError('link', {
                            type: 'validate',
                            message: intl.$t({
                                id: 'Composer.Composer.BadEmebdLinkError',
                                defaultMessage: 'Cannot embed this link',
                            }),
                        });
                    }
                } }, void 0)] }, void 0));
};
//# sourceMappingURL=Composer.js.map